import { useState } from 'react'
import { Button } from 'blaze-ui/Button'
import Styles from './BrandVisualStyleOptIn.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { BrandVisualOptionCreditsLabel } from './BrandVisualOptionCreditsLabel'

export interface BrandVisualStyleOptInProps {
  onNext: (visualStylesOptIn: boolean) => void
  onBack: () => void
  initialVisualStylesOptIn: boolean
  brandKitId: number
}

export const BrandVisualStyleOptIn = ({
  onNext,
  onBack,
  initialVisualStylesOptIn,
  brandKitId,
}: BrandVisualStyleOptInProps) => {
  const creditsEnabled = useFlipperFlag('credits_styles_videos')

  const [visualStylesOptIn, setVisualStylesOptIn] = useState(
    initialVisualStylesOptIn === undefined ? true : initialVisualStylesOptIn,
  )

  const { data: brandKitData } = useBrandKit(brandKitId)
  const { mutateAsync: updateBrandKitMutation } = useUpdateBrandKit({ id: brandKitId })

  const handleNext = () => {
    updateBrandKitMutation(
      {
        content_preferences: {
          ...brandKitData?.brand_kit?.content_preferences,
          apply_visual_style: !!visualStylesOptIn,
        },
      },
      {
        onSuccess: () => onNext(!!visualStylesOptIn),
      },
    )
    onNext(visualStylesOptIn)
  }

  return (
    <MultiStepStepLayout
      title="What content does your business need?"
      description="Choose the formats that will showcase your business. You can change these anytime."
      centerContent
      scrollContainerClassName={Styles.scrollContainer}
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <div className={Styles.footerSide}>
                <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
                  Back
                </Button>
              </div>
              <div className={Styles.footerSide}>
                <Button size="lg" onPress={handleNext} aria-label="Setup Your Channels">
                  Setup Your Channels
                </Button>
              </div>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.container}>
          <div
            className={cx(Styles.option, { [Styles.selected]: visualStylesOptIn })}
            onClick={() => setVisualStylesOptIn(true)}
          >
            <img
              src="https://res.cloudinary.com/almanac/image/upload/v1767729664/blaze_assets/virtual_marketer/onboarding/Frame_1171277062_fwmapt.png"
              alt="Option 1"
              className={Styles.optionImage}
            />
            {visualStylesOptIn && (
              <div className={Styles.radioBulletContainer}>
                <RadioBullet size={28} isSelected />
              </div>
            )}
            <div className={Styles.recommendedTag}>Recommended</div>
            <div className={Styles.optionContent}>
              <Heading level={2}>Stylized Videos & Images</Heading>
              <Text variant="primary">
                Get unlimited quantities of beautiful photos and videos that match your brand style and content.
              </Text>
              <BrandVisualOptionCreditsLabel />
            </div>
          </div>
          <div
            className={cx(Styles.option, { [Styles.selected]: !visualStylesOptIn })}
            onClick={() => setVisualStylesOptIn(false)}
          >
            <img
              src="https://res.cloudinary.com/almanac/image/upload/v1766521527/blaze_assets/virtual_marketer/onboarding/Frame_1171277062_tesojl.png"
              alt="Option 2"
              className={Styles.optionImage}
            />
            {!visualStylesOptIn && (
              <div className={Styles.radioBulletContainer}>
                <RadioBullet size={28} isSelected />
              </div>
            )}
            <div className={Styles.optionContent}>
              <Heading level={2}>Brand Kit & Stock Content</Heading>
              <Text variant="primary">For businesses that have a large quantity of great photos and videos.</Text>
              {creditsEnabled && (
                <Text variant="secondary" color="var(--dark-60)">
                  5 credits per image or video
                </Text>
              )}
            </div>
          </div>
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
